/**
 * BlogCard — Preview card for blog listing page.
 * Shows title, excerpt, category badge, reading time, date.
 */

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { BlogPostMeta } from '../../types/blog';
import { CATEGORY_CONFIG } from '../../types/blog';
import { useLangStore } from '../../store/langStore';

interface BlogCardProps {
  post: BlogPostMeta;
  featured?: boolean;
}

export function BlogCard({ post, featured = false }: BlogCardProps) {
  const { lang } = useLangStore();
  const cat = CATEGORY_CONFIG[post.category];
  const categoryLabel = lang === 'no' ? cat.labelNo : cat.labelEn;

  const formattedDate = new Date(post.date).toLocaleDateString(
    lang === 'no' ? 'nb-NO' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' },
  );

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className={`group ${featured ? 'col-span-full' : ''}`}
    >
      <Link
        to={`/undring/${post.slug}`}
        className="block rounded-2xl bg-white border border-gray-100 overflow-hidden
                   shadow-card hover:shadow-card-hover transition-all duration-300
                   hover:-translate-y-1"
      >
        {/* Image (if available) */}
        {post.image && (
          <div className="aspect-[16/9] overflow-hidden bg-sand-light">
            <img
              src={post.image}
              alt={post.imageAlt || post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
        )}

        <div className={`p-6 ${featured ? 'md:p-8' : ''}`}>
          {/* Category badge */}
          <div className="flex items-center gap-2 mb-3">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: cat.color }}
            >
              {cat.icon} {categoryLabel}
            </span>
          </div>

          {/* Title */}
          <h2
            className={`font-display font-bold text-ink leading-tight mb-2
                        group-hover:text-teal transition-colors
                        ${featured ? 'text-2xl md:text-3xl' : 'text-lg md:text-xl'}`}
          >
            {post.title}
          </h2>

          {/* Description */}
          <p className="text-ink-muted font-body text-sm leading-relaxed mb-4 line-clamp-2">
            {post.description}
          </p>

          {/* Meta row */}
          <div className="flex items-center gap-4 text-xs text-ink-faint font-display">
            <time dateTime={post.date}>{formattedDate}</time>
            <span aria-hidden="true">·</span>
            <span>{post.readingTime} min {lang === 'no' ? 'lesetid' : 'read'}</span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
